// Epic 32 — pure helpers for the per-user "Expected Weekly Work Time" (Soll)
// editor in Employee Management. Kept framework-free so the unset/validation
// logic is unit-testable without React/Redux/antd.

// Build the wire payload from the editor state, or return `null` to PRESERVE
// the "unset → fall back to the template Soll" semantics when the admin never
// gave this (previously-unset) user an expected work time.
//
// Without this guard, saving an untouched employee would send
// `{ defaultHours: 0, defaultMinutes: 0, entries: [] }`, which the backend
// treats as "set to 0h" — silently zeroing that employee's Soll everywhere.
//
// `entries` are `{ validFrom: "YYYY-MM-DD", hours, minutes }` (validFrom already
// formatted, or null). Entries are returned sorted ascending by date.
export function buildExpectedWorkTime({ defaultHours, defaultMinutes, entries, hadExisting }) {
  const sortedEntries = (entries || [])
    .map((e) => ({ validFrom: e.validFrom, hours: e.hours ?? 0, minutes: e.minutes ?? 0 }))
    .sort((a, b) => String(a.validFrom).localeCompare(String(b.validFrom)));

  const isEmpty =
    (defaultHours || 0) === 0 && (defaultMinutes || 0) === 0 && sortedEntries.length === 0;

  // Only collapse to `null` (unset) when the user had none to begin with.
  // A user who previously had a setting keeps it explicit (even at 0h) so an
  // admin can deliberately set 0.
  if (!hadExisting && isEmpty) return null;

  return {
    defaultHours: defaultHours || 0,
    defaultMinutes: defaultMinutes || 0,
    entries: sortedEntries,
  };
}

// Upper bound on the weekly hours field (a week is 168 hours — a weekly Soll
// can never exceed that). Guards against fat-fingered values.
export const MAX_WEEKLY_HOURS = 168;

// Validate the editor state. Returns an i18n key to show as an error, or `null`
// when valid. Rules (mirrors mobile + backend): minutes 0-59, hours 0-168,
// every entry has a valid-from, no duplicate valid-from dates.
export function validateExpectedWorkTime({ defaultHours, defaultMinutes, entries }) {
  const badTime = (h, m) =>
    h == null || m == null || h < 0 || h > MAX_WEEKLY_HOURS || m < 0 || m > 59;

  if (badTime(defaultHours, defaultMinutes)) return "expectedWeeklyWorkTime";

  const seen = new Set();
  for (const entry of entries || []) {
    if (!entry.validFrom) return "validFrom";
    if (badTime(entry.hours, entry.minutes)) return "expectedWeeklyWorkTime";
    if (seen.has(entry.validFrom)) return "duplicateValidFrom";
    seen.add(entry.validFrom);
  }
  return null;
}
